import Errored from '../core/Errored';
import Grid from '@material-ui/core/Grid';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import Warning from '../core/Warning';
import { APPLICATION_PATHS } from '../application-core/constants';
import ComponentTitle from '../core/ComponentTitle';
import { makeCancelable, processHashLocation } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import OntologyEditor from './OntologyEditor';

const tabClass = {
  0: "http://www.w3.org/2002/07/owl#Thing",
  1: "https://graphifi.com/schema/xowl/topAnnotationProperty",
  2: "http://www.w3.org/2002/07/owl#topDataProperty",
  3: "http://www.w3.org/2002/07/owl#topObjectProperty"
};

const tabValues = {
  0: "class",
  1: "annotationProperty",
  2: "datatypeProperty",
  3: "objectProperty"
};

const tabPaths = {
  0: "classTitlePath",
  1: "annotationTitlePath",
  2: "datatypeTitlePath",
  3: "objectTitlePath"
};

const tabActive = {
  0: "activeClass",
  1: "activeAnnotationProperty",
  2: "activeDatatypeProperty",
  3: "activeObjectProperty"
};

class OntologyEditorContainer extends Component {

  constructor(props) {
    super(props);

    this.state = {
      tabValue: 0,
      updateAt: null,
      foundRef: null,
      hasScrolled: false,
      viewWidth: 0,
      isFullView: false,
      deletedProperties: [],
      classTitlePath: [],
      objectTitlePath: [],
      datatypeTitlePath: [],
      annotationTitlePath: [],
      paths: [],
      viewLang: props.defaultLang,
      activeObjectProperty: null,
      activeDatatypeProperty: null,
      activeAnnotationProperty: null,
      activeClass: null,
      foundPath: null,
      isErrored: false,
      isWarning: false,
      fromPath: false
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    const paramsMap = processHashLocation(props.location);
    const owlClass = paramsMap.get("class");
    const objectProperty = paramsMap.get("objectProperty");
    const datatypeProperty = paramsMap.get("datatypeProperty");
    const annotationProperty = paramsMap.get("annotationProperty");
    const view = paramsMap.get("view");
    const tab = Object.keys(tabValues).find(key => tabValues[key] === view);
    if ((tab !== state.tabValue) || (objectProperty !== state.activeObjectProperty)
      || (datatypeProperty !== state.activeDatatypeProperty) || (annotationProperty !== state.activeAnnotationProperty)
      || (owlClass !== state.activeClass)) {
      return {
        ...(view) && { tabValue: parseInt(tab) },
        ...(state.activeClass !== owlClass) &&
        { activeClass: (owlClass === undefined || owlClass === null || owlClass === "") ? null : owlClass },
        ...(state.activeObjectProperty !== objectProperty) &&
        { activeObjectProperty: (objectProperty === undefined || objectProperty === null || objectProperty === "") ? null : objectProperty },
        ...(state.activeDatatypeProperty !== datatypeProperty) &&
        { activeDatatypeProperty: (datatypeProperty === undefined || datatypeProperty === null || datatypeProperty === "") ? null : datatypeProperty },
        ...(state.activeAnnotationProperty !== annotationProperty) &&
        { activeAnnotationProperty: (annotationProperty === undefined || annotationProperty === null || annotationProperty === "") ? null : annotationProperty }
      };
    }
    return null;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  buildLink = (view, iri = null) => {
    let newView = view || this.state.view;
    let newOwlClass = view === "class" && iri !== null ? iri : this.state.activeClass;
    let newObjectProperty = view === "objectProperty" && iri !== null ? iri : this.state.activeObjectProperty;
    let newDatatypeProperty = view === "datatypeProperty" && iri !== null ? iri : this.state.activeDatatypeProperty;
    let newAnnotationProperty = view === "annotationProperty" && iri !== null ? iri : this.state.activeAnnotationProperty;

    let link = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' +
      this.props.project + "&ontology=" + this.props.id + "&view=" + newView;
    link += newOwlClass !== null ? "&class=" + newOwlClass : "";
    link += newObjectProperty !== null ? "&objectProperty=" + newObjectProperty : "";
    link += newDatatypeProperty !== null ? "&datatypeProperty=" + newDatatypeProperty : "";
    link += newAnnotationProperty !== null ? "&annotationProperty=" + newAnnotationProperty : "";
    return link;
  };

  toggleOntologyDetail = () => this.props.toggleOntologyDetail(0);

  changeTab = (tabValue) => {
    const view = tabValues[tabValue];
    const link = this.buildLink(view);
    this.props.history.push(link);
  };

  doPath(payload) {
    this.cancellable = makeCancelable(GS.path(payload));
    return this.cancellable;
  }

  findNode = async (iri, topNodes, path, rootNode, pathData) => {
    // If it is a top node it will already be loaded
    if (topNodes.includes(iri)) {
      const expansionPath = "<" + iri + ">";
      this.setState({
        foundPath: expansionPath,
        foundRef: React.createRef(),
        foundTime: Date.now()
      });
      return [iri];
    }
    else {
      try {
        const searchData = pathData || await this.doPath({
          graph: this.props.graph,
          source: iri,
          path,
          target: this.props.id,
          targetPaths: [
            "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
            "https://grafsync.graphifi.com/schema/gs/visibility"
          ]
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (searchData.results) {
          const hasTopNodeResult = searchData.results.find(result => topNodes.includes(result.parent));
          if (hasTopNodeResult) {
            const hasTopNode = hasTopNodeResult.parent;
            const expansionPath = this.getExpansionTree(hasTopNode, searchData.results, iri, "<" + hasTopNode + ">");
            this.getFoundNode(expansionPath);
            // Now select the found resource
            let pathParts = expansionPath.substring(1, expansionPath.length - 1).split("><");
            return pathParts;
          }
          else {
            const rootNodeResult = searchData.results.find(result => result.parent === rootNode);
            if (rootNodeResult) {
              const hasTopNode = rootNodeResult.child;
              const expansionPath = this.getExpansionTree(hasTopNode, searchData.results, iri, "<" + hasTopNode + ">");
              this.getFoundNode(expansionPath);
              // Now select the found resource
              let pathParts = expansionPath.substring(1, expansionPath.length - 1).split("><");
              return pathParts;
            }
          }
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({
          isErrored: true,
          message: "There was an issue processing information for a resource."
        });
      }
    }
  };

  getFoundNode(expansionPath, ignoreFirst = false) {
    // We need to expand all the nodes down to the found node if not already expanded
    let paths = cloneDeep(this.state.paths);
    let pathParts = expansionPath.substring(1, expansionPath.length - 1).split("><");
    pathParts.pop();
    let path = "";
    pathParts.forEach((part, index) => {
      if (index === 0 && ignoreFirst) return;
      path += "<" + part + ">";
      if (!paths.includes(path)) paths.push(path);
    });
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.setState({ paths }, () => {
      // Only when we have expanded do we try and find
      this.setState({
        foundPath: expansionPath,
        foundRef: React.createRef(),
        foundTime: Date.now()
      });
    });
  }

  getExpansionTree(parentNode, results, foundIRI, path = "") {
    let subnodes = results.filter(result => result.parent === parentNode).map(result => result.child);
    if (!subnodes || subnodes.length === 0) return path;
    if (subnodes.includes(foundIRI)) return path + "<" + foundIRI + ">";
    return this.getExpansionTree(subnodes[0], results, foundIRI, path + "<" + subnodes[0] + ">");
  }

  onExitWarning = () => this.setState({ isWarning: false });

  // If pathData is passed it is the hoped for path to the resource
  setFound = (tab) => (topNodes, rootNode) => async (iri, titlePath) => {
    if (titlePath && titlePath.length > 0) {
      this.showDetail(tab)(iri, [...titlePath, iri]);
      const path = [...titlePath, iri].map(p => "<" + p + ">").join("");
      this.getFoundNode(path);
      this.setState({
        fromPath: true,
        classTitlePath: [...titlePath, iri]
      });
      return;
    }
    this.showDetail(tab)(iri);
    switch (tab) {
      case 0: {
        const classFoundPath = await this.findNode(iri, topNodes, "http://www.w3.org/2000/01/rdf-schema#subClassOf", rootNode);
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (classFoundPath) this.setState({ classTitlePath: classFoundPath });
        break;
      }
      case 1: {
        const annotationFoundPath = await this.findNode(iri, topNodes, "https://graphifi.com/schema/xowl/subAnnotationPropertyOf", rootNode);
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (annotationFoundPath) this.setState({ annotationTitlePath: annotationFoundPath });
        break;
      }
      case 2: {
        const datatypeFoundPath = await this.findNode(iri, topNodes, "http://www.w3.org/2000/01/rdf-schema#subPropertyOf", rootNode);
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (datatypeFoundPath) this.setState({ datatypeTitlePath: datatypeFoundPath });
        break;
      }
      case 3: {
        const objectFoundPath = await this.findNode(iri, topNodes, "http://www.w3.org/2000/01/rdf-schema#subPropertyOf", rootNode);
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (objectFoundPath) this.setState({ objectTitlePath: objectFoundPath });
        break;
      }
      default: return;
    }
  };

  setViewLanguage = (lang) => {
    this.setState({ viewLang: lang });
  };

  componentDidUpdate() {
    const tab = this.state.tabValue;
    const path = this.state[tabPaths[tab]];
    const activeProp = tabActive[tab];
    if (!this.state.fromPath && path && path.length > 0 && this.state[activeProp] !== path[path.length - 1]) {
      this.setState({ [tabPaths[tab]]: [] }, () => {
        if (this.state[activeProp]) this.setFound(tab)([], tabClass[tab])(this.state[activeProp]);
      });
    }
  }

  expandNode = (path) => {
    let paths = cloneDeep(this.state.paths);
    paths.push(path);
    this.setState({ paths });
  };

  collapseNode = (collapsePath) => {
    let paths = cloneDeep(this.state.paths);
    let pathIndex = paths.indexOf(collapsePath);
    paths.splice(pathIndex, 1);
    this.setState({ paths });
  };

  showDetail = (tab) => (iri, path, runSearch = true) => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.setState({ runSearch }, () => {
      const link = this.buildLink(tabValues[tab], iri);
      const currentPath = this.props.location.pathname + this.props.location.hash;
      if (currentPath !== link) this.props.history.push(link);
      this.setState({
        ...(path) && { [tabPaths[tab]]: [...path] },
        runSearch: true,
        fromPath: false
      });
    });
  };

  breakLink = (property) => (parentIRI, childIRI) => {
    if (property === "hasSubAnnotationProperty") {
      // For annotation properties we also get rid of any rdfs:subProperty inverse relationships
      const linkData = GS.data(parentIRI, this.props.graph);
      if (linkData.hasSubProperty && linkData.hasSubProperty.includes(childIRI)) {
        GS.deleteProperty(parentIRI, this.props.graph, "hasSubProperty", childIRI);
      }
      // It might be the case that the subPropertyOf exists but not the hasSubProperty
      // So check for that too.
      else if (GS.isLoaded(childIRI, this.props.graph)) {
        const linkChildData = GS.data(childIRI, this.props.graph);
        if (linkChildData.subPropertyOf)
          if ((Array.isArray(linkChildData.subPropertyOf) && linkChildData.subPropertyOf.includes(parentIRI)) ||
            linkChildData.subPropertyOf === parentIRI) {
            GS.deleteProperty(childIRI, this.props.graph, "subPropertyOf", parentIRI);
          }
      }
    }
    GS.deleteProperty(parentIRI, this.props.graph, property, childIRI);
  };

  addExistingNode = (property) => (parentIRI, childIRI) => {
    GS.loadData([childIRI], {}, this.props.graph).then(() => {
      if (GS.isLoaded(childIRI, this.props.graph)) {
        GS.addProperty(parentIRI, this.props.graph, property, childIRI);
      }
      else {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({
          isWarning: true,
          message: "This resource has just been deleted and cannot be added."
        });
      }
    });
  };

  addNewResource = async (label, resourceIRI, reservationID, resourceClass) => {
    const newResource = {
      id: resourceIRI,
      type: resourceClass,
      isDefinedBy: this.props.id,
      label: { [this.props.defaultLang]: label }
    };
    await GS.insertResource(newResource, resourceIRI, this.props.graph, [], reservationID);
    return resourceIRI;
  };

  doFind(payload) {
    this.cancellable = makeCancelable(GS.find(payload));
    return this.cancellable;
  }

  addNewNode = (tab) => (titlePath, path) => async (parentIRI, resourceIRI, reservationID, label) => {
    let iri;
    switch (tab) {
      case 0:
        iri = await this.addNewResource(label, resourceIRI, reservationID, "OwlClass");
        GS.addProperty(parentIRI, this.props.graph, "hasSubClass", iri);
        break;
      case 1:
        iri = await this.addNewResource(label, resourceIRI, reservationID, "AnnotationProperty");
        GS.addProperty(parentIRI, this.props.graph, "hasSubAnnotationProperty", iri);
        break;
      case 2:
        iri = await this.addNewResource(label, resourceIRI, reservationID, "DatatypeProperty");
        GS.addProperty(parentIRI, this.props.graph, "hasSubProperty", iri);
        break;
      case 3:
        iri = await this.addNewResource(label, resourceIRI, reservationID, "ObjectProperty");
        GS.addProperty(parentIRI, this.props.graph, "hasSubProperty", iri);
        break;
      default: return;
    }
    this.showDetail(tab)(iri, [...titlePath, iri]);
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.expandNode(path);
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.setState({
      foundPath: path + "<" + iri + ">",
      foundRef: React.createRef(),
      foundTime: Date.now()
    });
  };

  deleteNode = (iri) => {
    GS.deleteResource(iri, this.props.graph);
    // We need to remove all expansion paths that include this IRI
    let paths = cloneDeep(this.state.paths);
    paths.filter(path => !path.includes("<" + iri + ">"));
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.setState({ paths });
    let link = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' +
      this.props.project + "&ontology=" + this.props.id;
    this.props.history.push(link);
  };

  collapse = () => {
    this.setState({
      paths: []
    });
  };

  onFullViewChange = () => this.setState({ isFullView: !this.state.isFullView });

  onViewWidthChange = () => this.setState({ viewWidth: this.state.viewWidth === 3 ? 0 : this.state.viewWidth + 1 });

  render() {

    const { isErrored, tabValue, activeClass, paths, foundRef, foundPath, message, foundTime, isFullView, viewWidth,
      classTitlePath, objectTitlePath, datatypeTitlePath, annotationTitlePath, viewLang, isWarning,
      activeDatatypeProperty, activeObjectProperty, activeAnnotationProperty } = this.state;

    const { project, data, defaultLang, id, graph } = this.props;

    const iriOptions = {
      initialLowerCase: data.propertiesInitialLowerCase,
      removeSpaces: data.iriRemoveSpaces,
      replaceSpaces: data.iriReplaceSpaces,
      removeSpecialCharacters: data.iriRemoveSpecialCharacters,
      replaceSpecialCharacters: data.iriReplaceSpecialCharacters,
      minimiseEncoding: data.minimiseEncoding,
    };

    if (isErrored) return <Errored data={{ message }} />;
    if (isWarning) return <Warning message={message} onExitWarning={this.onExitWarning} />;

    return (
      <React.Fragment>
        <ComponentTitle title={data.title} id={data.id} viewLang={viewLang} defaultLang={defaultLang} onClick={this.toggleOntologyDetail} />
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <OntologyEditor
              buildLink={this.buildLink}
              project={project}
              iriOptions={iriOptions}
              availableAnnotationProperty={data.availableAnnotationProperty}
              hiddenProps={data.hiddenProp}
              graph={graph}
              onSelectViewLanguage={this.setViewLanguage}
              viewLang={viewLang}
              location={this.props.location}
              toggleOntologyDetail={this.toggleOntologyDetail}
              ontologyIRI={id}
              excludeDeprecated={data.excludeDeprecated}
              useSlashIRI={data.useSlashIRI || false}
              useUuidIRI={data.useUuidIRI || false}
              foundTime={foundTime}
              expandNode={this.expandNode}
              expandedPaths={paths}
              foundPath={foundPath}
              foundRef={foundRef}
              selectedTitlePath={
                [classTitlePath, annotationTitlePath, datatypeTitlePath, objectTitlePath]
              }
              onBreakLink={this.breakLink}
              collapseNode={this.collapseNode}
              onAddExistingNode={this.addExistingNode}
              onAddNewNode={this.addNewNode}
              onDeleteNode={this.deleteNode}
              onFound={this.setFound}
              onChangeTab={this.changeTab}
              onShowDetail={this.showDetail}
              isFullView={isFullView}
              viewWidth={viewWidth}
              onCollapse={this.collapse}
              onViewWidthChange={this.onViewWidthChange}
              onFullViewChange={this.onFullViewChange}
              tabValue={tabValue}
              activeClass={activeClass}
              activeDatatypeProperty={activeDatatypeProperty}
              activeObjectProperty={activeObjectProperty}
              activeAnnotationProperty={activeAnnotationProperty}
              defaultLang={defaultLang} />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }

}

OntologyEditorContainer.propTypes = {
  id: PropTypes.string.isRequired,
  location: PropTypes.object,
  history: PropTypes.object,
  toggleOntologyDetail: PropTypes.func,
  graph: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(withGS(OntologyEditorContainer));